'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import {
  ScanLine,
  ArrowLeft,
  CheckCircle2,
  Clock,
  ShieldCheck,
  X
} from 'lucide-react';
import { useRouter } from '@/i18n/routing';
import { useAccessibleRoute } from '@/hooks/use-route-access';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageActionBar } from '@/components/layout/page-action-bar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  useCompleteSessionMutation,
  useVerifyItemMutation
} from '../services/reception.mutations';
import type { ReceptionSession, ReceptionSessionItem } from '../types';

interface StepDataVerificationProps {
  session: ReceptionSession;
  onBack?: () => void;
  onSessionUpdate?: (session: ReceptionSession) => void;
}

export function StepDataVerification({
  session,
  onBack,
  onSessionUpdate
}: StepDataVerificationProps) {
  const router = useRouter();
  const backTo = useAccessibleRoute();
  const completeSessionMutation = useCompleteSessionMutation();
  const t = useTranslations('reception');
  const [containerIdentifier, setContainerIdentifier] = useState('');
  const scannerInputRef = useRef<HTMLInputElement>(null);
  const [itemOverrides, setItemOverrides] = useState<
    Map<number, ReceptionSessionItem>
  >(new Map());

  const [pendingSealItem, setPendingSealItem] =
    useState<ReceptionSessionItem | null>(null);
  const [seal1, setSeal1] = useState('');
  const [seal2, setSeal2] = useState('');
  const [sealError, setSealError] = useState<string | null>(null);

  useEffect(() => {
    scannerInputRef.current?.focus();
  }, []);

  const didAutoCompleteRef = useRef(false);

  const baseItems = session.items ?? [];
  const localItems = baseItems.map(
    (item) => itemOverrides.get(item.id) ?? item
  );
  for (const [id, item] of itemOverrides) {
    if (!baseItems.some((b) => b.id === id)) {
      localItems.push(item);
    }
  }

  const verifyItemMutation = useVerifyItemMutation();

  const totalItems = localItems.length;
  const matchedItems = localItems.filter(
    (item) => item.status === 'matched'
  ).length;
  const completionPercentage =
    totalItems > 0 ? Math.round((matchedItems / totalItems) * 100) : 0;
  const isComplete = totalItems > 0 && matchedItems === totalItems;

  // Auto-close the session as soon as every item is matched.
  useEffect(() => {
    if (!isComplete || didAutoCompleteRef.current) return;
    if (completeSessionMutation.isPending) return;
    didAutoCompleteRef.current = true;
    completeSessionMutation
      .mutateAsync(session.id)
      .then(() => router.push(backTo('/dashboard/shipment-statement')))
      .catch(() => {
        // Allow manual retry via the button if the auto-complete failed.
        didAutoCompleteRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete, session.id]);

  const resetSealState = () => {
    setPendingSealItem(null);
    setSeal1('');
    setSeal2('');
    setSealError(null);
  };

  const applyItemUpdate = (updatedItem: ReceptionSessionItem) => {
    setItemOverrides((prev) => new Map(prev).set(updatedItem.id, updatedItem));

    if (onSessionUpdate) {
      const updatedItems = localItems.slice();
      const existingIdx = updatedItems.findIndex(
        (i) => i.id === updatedItem.id
      );
      if (existingIdx >= 0) {
        updatedItems[existingIdx] = updatedItem;
      } else {
        updatedItems.push(updatedItem);
      }
      onSessionUpdate({ ...session, items: updatedItems });
    }
  };

  const submitVerify = async (
    identifier: string,
    seals?: { seal1: string; seal2: string }
  ) => {
    let result;
    try {
      result = await verifyItemMutation.mutateAsync({
        sessionId: session.id,
        data: {
          container_identifier: identifier,
          ...(seals?.seal1 && { security_seal_1: seals.seal1 }),
          ...(seals?.seal2 && { security_seal_2: seals.seal2 })
        }
      });
    } catch (err: unknown) {
      // Keep the seal card open and surface the server message for seal-related flows.
      if (seals) {
        const message = err instanceof Error ? err.message : t('sealsMismatch');
        setSealError(message);
      }
      scannerInputRef.current?.focus();
      return;
    }

    if (result?.data) {
      applyItemUpdate(result.data);
    }

    resetSealState();
    setContainerIdentifier('');
    scannerInputRef.current?.focus();
  };

  const handleScanVerify = async () => {
    const identifier = containerIdentifier.trim();
    if (!identifier) return;

    const alreadyMatched = localItems.find(
      (item) =>
        item.container_identifier === identifier && item.status === 'matched'
    );
    if (alreadyMatched) {
      toast.info(t('alreadyMatched'));
      setContainerIdentifier('');
      scannerInputRef.current?.focus();
      return;
    }

    const matchedItem = localItems.find(
      (item) => item.container_identifier === identifier
    );

    if (matchedItem?.has_security_seals) {
      setPendingSealItem(matchedItem);
      setSeal1('');
      setSeal2('');
      setSealError(null);
      return;
    }

    await submitVerify(identifier);
  };

  const handleConfirmSeals = async () => {
    if (!pendingSealItem) return;
    if (pendingSealItem.has_seal_1 && !seal1.trim()) {
      setSealError(t('sealsRequired'));
      return;
    }
    if (pendingSealItem.has_seal_2 && !seal2.trim()) {
      setSealError(t('sealsRequired'));
      return;
    }
    setSealError(null);
    await submitVerify(pendingSealItem.container_identifier, {
      seal1: seal1.trim(),
      seal2: seal2.trim()
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleScanVerify();
    }
  };

  return (
    <>
      <div className='flex w-full flex-1 flex-col gap-6'>
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <ScanLine className='h-5 w-5' />
                {t('dataVerificationTitle')}
              </div>
              <Badge
                variant={isComplete ? 'default' : 'secondary'}
                className={cn(
                  isComplete && 'bg-green-500 text-white hover:bg-green-500/80'
                )}
              >
                {completionPercentage}%
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='bg-muted h-2 w-full overflow-hidden rounded-full'>
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-300',
                  isComplete ? 'bg-green-500' : 'bg-primary'
                )}
                style={{ width: `${completionPercentage}%` }}
              />
            </div>

            <p className='text-muted-foreground text-sm'>
              {t('matchedItemsCount', {
                matched: matchedItems,
                total: totalItems
              })}
            </p>

            <div className='space-y-2'>
              <Label htmlFor='container-identifier'>
                {t('containerIdentifier')}
              </Label>
              <div className='flex gap-2'>
                <Input
                  ref={scannerInputRef}
                  id='container-identifier'
                  value={containerIdentifier}
                  onChange={(e) => setContainerIdentifier(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('scanContainerPlaceholder')}
                  disabled={isComplete || !!pendingSealItem}
                />
                <Button
                  onClick={handleScanVerify}
                  disabled={
                    isComplete ||
                    !containerIdentifier.trim() ||
                    verifyItemMutation.isPending ||
                    !!pendingSealItem
                  }
                >
                  {verifyItemMutation.isPending
                    ? t('verifying')
                    : t('scanAndVerify')}
                </Button>
              </div>
              <p className='text-muted-foreground text-xs'>
                {t('scannerHint')}
              </p>
            </div>

            {totalItems > 0 && (
              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('containerIdentifier')}</TableHead>
                      <TableHead>{t('label')}</TableHead>
                      <TableHead>{t('status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {localItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className='font-mono text-sm'>
                          {item.container_identifier}
                        </TableCell>
                        <TableCell>{item.label ?? '-'}</TableCell>
                        <TableCell>
                          {item.status === 'matched' ? (
                            <Badge
                              variant='default'
                              className='bg-green-500 text-white hover:bg-green-500/80'
                            >
                              <CheckCircle2 className='me-1 h-3 w-3' />
                              {t('statusMatched')}
                            </Badge>
                          ) : (
                            <Badge variant='secondary'>
                              <Clock className='me-1 h-3 w-3' />
                              {t('statusPending')}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {pendingSealItem && (
          <Card className='border-primary/40'>
            <CardHeader>
              <CardTitle className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <ShieldCheck className='h-5 w-5' />
                  {t('securitySealsTitle')}
                </div>
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={resetSealState}
                  aria-label={t('cancel')}
                >
                  <X className='h-4 w-4' />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <p className='text-muted-foreground text-sm'>
                {t('securitySealsHint', {
                  box: pendingSealItem.container_identifier
                })}
              </p>
              <div className='grid gap-3 sm:grid-cols-2'>
                {pendingSealItem.has_seal_1 && (
                  <div className='space-y-1'>
                    <Label htmlFor='security-seal-1'>
                      {t('securitySeal1Label')}
                    </Label>
                    <Input
                      id='security-seal-1'
                      value={seal1}
                      onChange={(e) => setSeal1(e.target.value)}
                      autoFocus
                    />
                  </div>
                )}
                {pendingSealItem.has_seal_2 && (
                  <div className='space-y-1'>
                    <Label htmlFor='security-seal-2'>
                      {t('securitySeal2Label')}
                    </Label>
                    <Input
                      id='security-seal-2'
                      value={seal2}
                      onChange={(e) => setSeal2(e.target.value)}
                    />
                  </div>
                )}
              </div>
              {sealError && (
                <p className='text-destructive text-sm font-medium'>
                  {sealError}
                </p>
              )}
              <div className='flex justify-end gap-2'>
                <Button variant='outline' onClick={resetSealState}>
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleConfirmSeals}
                  disabled={verifyItemMutation.isPending}
                >
                  {verifyItemMutation.isPending
                    ? t('verifying')
                    : t('confirmSeals')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <PageActionBar
          start={
            onBack ? (
              <Button variant='outline' onClick={onBack}>
                <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
                {t('backToBoxCount')}
              </Button>
            ) : null
          }
        >
          <Button
            onClick={async () => {
              await completeSessionMutation.mutateAsync(session.id);
              router.push(backTo('/dashboard/shipment-statement'));
            }}
            disabled={!isComplete || completeSessionMutation.isPending}
          >
            {completeSessionMutation.isPending
              ? t('completing')
              : t('confirmAndCloseSession')}
          </Button>
        </PageActionBar>
      </div>
    </>
  );
}
